import { Injectable }                   from '@angular/core';
import {
	BusinessPremisesDetailPageErsExternalFormPageCreateRoute,
	BusinessPremisesDetailPageErsExternalFormPageUpdateRoute,
	BusinessPremisesDetailPageErsFormPageCreateBaseRoute,
	BusinessPremisesDetailPageErsFormPageCreateRoute,
	BusinessPremisesDetailPageErsFormPageUpdateBaseRoute,
	BusinessPremisesDetailPageErsFormPageUpdateRoute,
	BusinessPremisesDetailPageProductTabRoute,
	BusinessPremisesDetailPageProductTabVariantRoute,
	BusinessPremisesRoutesEnum
}                                       from '@app/modules/business-premises/routes/business-premises.routes';
import {
	ERS_METADATA,
	IErsMetadata
}                                       from '@app/modules/ers/config/ers.config';
import { ErsLifecycleActionsEnum }      from '@app/modules/ers/modals/ers-lifecycle-modal/ers-lifecycle-modal.model';
import {
	Ers,
	ErsExternal,
	ErsExternalStatusEnum,
	ErsPresetEnum,
	ErsStatus,
	ErsStatusEnum,
	ErsVariant
}                                       from '@app/modules/ers/models/ers.model';
import { ErsDetailsConfig }             from '@app/modules/ers/presets/ers-details-config.model';
import {
	ErsDetailPageBaseRoute,
	ErsDetailPageRoute,
	ErsDetailPageTabBaseRoute,
	ErsDetailPageTabRoute,
	ErsExternalDetailPageRoute,
	ErsExternalDetailPageTabRoute,
	ErsExternalFormPageCreateRoute,
	ErsExternalFormPageUpdateRoute,
	ErsExternalListPageRoute,
	ErsFormPageCreateBaseRoute,
	ErsFormPageCreateRoute,
	ErsFormPageUpdateBaseRoute,
	ErsFormPageUpdateRoute,
	ErsListPageBaseRoute,
	ErsListPageRoute,
	ErsRoutesEnum
}                                       from '@app/modules/ers/routes/ers.routes';
import {
	IPropsChangeStatusErsEntity,
	IPropsUpdateErsState,
	PropsErsLifecycleType
}                                       from '@app/modules/ers/store/ers.actions';
import { ersStore }                     from '@app/modules/ers/store/ers.store';
import { ImportTypeEnum }               from '@app/modules/structure-import/models/structure-import.model';
import { AbstractBaseService }          from '@core/bases/services/abstractBaseService';
import {
	IErsHttpParams,
	RequestCompleteEntityState,
	RequestCompleteListState,
	RequestState
}                                       from '@core/interfaces/api.interface';
import {
	AllowedDetailsTabEnum,
	ObservableTypeEnum,
	ProductEnum
}                                       from '@core/interfaces/common.interfaces';
import {
	IRouteDefinition,
	RouteBasePath,
	RouteDefinition
}                                       from '@core/interfaces/route.interface';
import {
	IStoreConfig,
	IStoreProcessingConfig,
	StoreActionsEnum
}                                       from '@core/interfaces/store.interface';
import { AuthorizationService }         from '@core/services/authorization/authorization.service';
import { ErsApiService }                from '@http/ers/ers-api.service';
import { ErsExternalApiService }        from '@http/ers/ers-external-api.service';
import { EntityVariantEnum }            from '@models/entity.model';
import { PresetNavigateBackConfig }     from '@models/preset';
import {
	select,
	Store
}                                       from '@ngrx/store';
import {
	IChangeStatusErsCallbackOptions,
	IStatusFlowChangeEvent
}                                       from '@shared/components/status-flow/status-flow.model';
import { StatusFlowService }            from '@shared/components/status-flow/status-flow.service';
import { ListOperationsIdentifierEnum } from '@shared/list-operations/model/list-operations.model';
import { actionStatus }                 from '@shared/services/toaster.service';
import { EntityUtils }                  from '@utils/helpers';
import { Observable }                   from 'rxjs';
import { take }                         from 'rxjs/operators';


@Injectable( {
	providedIn: 'root'
} )
export class ErsService extends AbstractBaseService {

	private readonly ersMetadata: IErsMetadata = ERS_METADATA;

	constructor(
		private readonly _authorizationService: AuthorizationService,
		private readonly _ersApi: ErsApiService,
		private readonly _ersExternalApi: ErsExternalApiService,
		private readonly _statusFlowService: StatusFlowService,
		protected readonly store$: Store
	) {
		super( store$, ersStore.selectors, ersStore.actions );
		this.logger.trace( 'ErsService:constructor' );
	}

	// ACTIONS ###########################################################################
	private changeErsStatus(
		callbackOptions: IChangeStatusErsCallbackOptions,
		statusChangeEvent: IStatusFlowChangeEvent<ErsStatus>
	): void {
		const ers: ErsVariant = callbackOptions.entity as ErsVariant;
		let lifecycleAction: ErsLifecycleActionsEnum;


		const changeStatusErsEntityProps: IPropsChangeStatusErsEntity = {
			callback: this.changeStatusCallback,
			callbackOptions,
			caller: callbackOptions?.caller + ' | ErsService:changeErsStatus->showErsLifecycle',
			targetStatus: statusChangeEvent.targetStatus
		};


		switch ( statusChangeEvent.targetStatus ) {
			// Active case
			case EntityUtils.getValueByEntityVariant<ErsExternalStatusEnum, ErsStatusEnum>(
				ers.entityVariant,
				ErsExternalStatusEnum.ACTIVE,
				ErsStatusEnum.ACTIVE
			):
				lifecycleAction = ErsLifecycleActionsEnum.ACTIVATE;

				EntityUtils.executeByEntityVariant(
					ers.entityVariant,
					() => this.dispatchErsLifecycle( {
						...changeStatusErsEntityProps,
						lifecycleAction
					} ),
					() => this.changeStatusDefault( callbackOptions, statusChangeEvent )
				);
				return;

			// Disable case
			case EntityUtils.getValueByEntityVariant<ErsExternalStatusEnum, ErsStatusEnum>(
				ers.entityVariant,
				ErsExternalStatusEnum.DISABLED,
				ErsStatusEnum.DISABLED
			):
				lifecycleAction = ErsLifecycleActionsEnum.DISABLE;
				EntityUtils.executeByEntityVariant(
					ers.entityVariant,
					() => this.dispatchErsLifecycle( {
						...changeStatusErsEntityProps,
						lifecycleAction
					} ),
					() => this.changeStatusDefault( callbackOptions, statusChangeEvent )
				);

				return;

			default:
				this.changeStatusDefault( callbackOptions, statusChangeEvent );
				return;
		}
	}

	private dispatchErsLifecycle( ersLifecycleTypeProps: PropsErsLifecycleType ): void {
		this.store$.dispatch(
			ersStore.actions.showErsLifecycle( ersLifecycleTypeProps )
		);
	}

	private changeStatusDefault(
		callbackOptions: IChangeStatusErsCallbackOptions,
		statusChangeEvent: IStatusFlowChangeEvent<ErsStatus>
	): void {
		this._statusFlowService.changeStatus(
			{
				callback: this.changeStatusCallback,
				callbackOptions,
				caller: callbackOptions?.caller,
				entity: callbackOptions?.entity,
				entityId: this.ersMetadata.entityId,
				entityService: this,
				statusChangeEvent
			}
		);
	}

	private changeStatusCallback(
		service: ErsService,
		callbackOptions: IChangeStatusErsCallbackOptions
	): void {
		const {
			caller,
			entity,
			listOperationsId,
			presetId
		} = { ...callbackOptions };

		const ers: ErsVariant = entity as ErsVariant;

		if ( ers ) {
			if ( listOperationsId ) {
				service.uiService.showPageLoaderAndCountUp();
				service.searchWithStoredListOperationsData(
					{
						businessPremisesId: ers.businessPremisesId,
						caller,
						listOperationsId,
						presetId,
						skipErsActionItemChecks: false
					}
				)
				.pipe( take( 1 ) )
				.subscribe( ( ersListRefreshState: RequestCompleteListState<Ers> ) => {
					service.uiService.hidePageLoaderOrCountDown();
				} );
			} else {
				service.checkAndUpdateEntityState(
					ers,
					caller
				);
			}
		}
	}

	private deleteErs(
		options: {
			ersId: string,
			caller: string,
			businessPremisesId?: string,
			listOperationsId?: ListOperationsIdentifierEnum,
			navigateToList?: boolean,
			presetId: ErsPresetEnum
		}
	): void {
		const {
			ersId,
			caller,
			businessPremisesId,
			listOperationsId,
			navigateToList,
			presetId
		} = options;

		const dialogConfirmationText = this.translationService.getI18nString(
			true,
			ERS_METADATA.i18n.basepath,
			'entity.dialogForDelete.description'
		);
		this.dialogCommonService.showConfirmationDialog( dialogConfirmationText, 'delete' )
		.pipe( take( 1 ) )
		.subscribe( res => {
			this.uiService.showPageLoaderAndCountUp();
			this.delete( {
				ersId,
				presetId,
				storeConfig: { updateStore: true }
			} )
			.pipe( take( 1 ) )
			.subscribe( ( state: RequestCompleteEntityState<ErsVariant> ) => {
				if ( state.requestState === RequestState.SUCCESS ) {
					this.storeRemoveOne<ErsVariant>( ersId );
					if ( businessPremisesId ) {
						this.store$.dispatch( ersStore.actions.updateBusinessPremisesActionItemsState( {
							businessPremisesId,
							caller: 'ErsService:deleteErs->success'
						} ) );


						// update list and paging data
						if ( listOperationsId ) {
							this.searchWithStoredListOperationsData(
								{
									businessPremisesId,
									caller: 'ErsService:deleteErs->success',
									listOperationsId,
									presetId,
									skipErsActionItemChecks: true
								}
							)
							.pipe( take( 1 ) )
							.subscribe();
						}
					}

					if ( navigateToList ) { this.navigateBack( { caller } ); }
				}
				this.toasterService.dispatch( state, this.ersMetadata.entityId, actionStatus.DELETE );
				this.uiService.hidePageLoaderOrCountDown();
			} );
		} );
	}

	private pageReadErsHandler( readReq$: Observable<RequestCompleteEntityState<ErsVariant>>, navigateBackFallback: PresetNavigateBackConfig ): void {
		readReq$
		.pipe( take( 1 ) )
		.subscribe( ( ersState: RequestCompleteEntityState<ErsVariant> ) => {
			this.uiService.hidePageLoaderOrCountDown();
			// on fail redirects to list
			if ( ersState.requestState === RequestState.FAILED ) {
				this.toasterService.dispatch( ersState, this.ersMetadata.entityId, actionStatus.READ );
				this.navigateBack( { caller: 'ErsService:pageReadErsHandler', navigateBackFallback } );
			}
		} );
	}

	readErsOnDetailsPage( ersId: string, ersDetailsConfig: ErsDetailsConfig ): void {
		const { navigateBack, presetId } = { ...ersDetailsConfig };

		this.uiService.showPageLoaderAndCountUp();
		const readReq$ = this.read( { id: ersId, presetId, skipErsActionItemChecks: false, withFullResponse: false } );
		this.pageReadErsHandler( readReq$, navigateBack );
	}

	deleteErsOnDetailPage(
		options: {
			caller: string,
			ersId: string,
			presetId: ErsPresetEnum
		}
	): void {
		this.deleteErs( { ...options, navigateToList: true } );
	}

	deleteErsOnEntity(
		options: {
			businessPremisesId: string,
			caller: string,
			ersId: string,
			listOperationsId?: ListOperationsIdentifierEnum,
			presetId: ErsPresetEnum
		} ): void {
		this.deleteErs( { ...options, navigateToList: false } );
	}


	changeErsStatusOnDetailPage(
		options: {
			ers: ErsVariant,
			caller: string,
			statusChangeEvent: IStatusFlowChangeEvent<ErsStatus>,
			presetId: ErsPresetEnum
		}
	): void {
		this.changeErsStatus(
			{
				caller: options?.caller,
				entity: options?.ers,
				presetId: options?.presetId
			},
			options?.statusChangeEvent
		);
	}

	changeErsStatusOnEntity(
		options: {
			ers: ErsVariant,
			caller: string,
			statusChangeEvent: IStatusFlowChangeEvent<ErsStatus>,
			listOperationsId?: ListOperationsIdentifierEnum,
			presetId: ErsPresetEnum
		}
	): void {
		this.changeErsStatus(
			{
				caller: options?.caller,
				entity: options?.ers,
				listOperationsId: options?.listOperationsId,
				presetId: options?.presetId
			},
			options?.statusChangeEvent
		);
	}

	changeErsStatusOnErsLifecycleModal( ers: ErsVariant, presetId: ErsPresetEnum ): Observable<RequestCompleteEntityState<ErsVariant>> {
		// return this.changeStatus( ers, ers.status, null, presetId );
		return this.executeApiRequestByPresetId(
			presetId,
			() => this.changeStatus( ers, ers.status, null, presetId ),
			() => this.update( { ers: { ...ers, status: ers.status }, presetId, withFullResponse: true } )
		);
	}

	// Modals
	showStructureImportForErs( importType: ImportTypeEnum.ERS | ImportTypeEnum.ERS_EXTERNAL ): void {
		this.store$.dispatch(
			ersStore.actions.structureImportForErs(
				{
					caller: 'ErsService:showStructureImportForErs',
					importType
				}
			)
		);
	}


	// CHECKS #################################################################################
	checkAndUpdateEntityState( ers: ErsVariant, caller: string ): void {
		caller += ' | ErsService:checkAndUpdateEntityState';
		this.store$.dispatch(
			ersStore.actions.checkAndUpdateEntityState( { ers, caller } )
		);
	}

	// HELPERS ################################################################################
	isInStatusState( ers: ErsVariant, variantsStatusMap: Map<EntityVariantEnum, ErsStatus> ): boolean {
		if ( !ers ) { return false; }

		const statusEnum: ErsStatus = variantsStatusMap.get( ers.entityVariant );
		return statusEnum === ers.status;
	}

	isInStatusStateActive( ers: ErsVariant ): boolean {
		const variantsStatusMap: Map<EntityVariantEnum, ErsStatus> =
			new Map<EntityVariantEnum, ErsStatus>( [
				[ EntityVariantEnum.EXTERNAL, ErsExternalStatusEnum.ACTIVE ],
				[ EntityVariantEnum.INTERNAL, ErsStatusEnum.ACTIVE ]
			] );

		return this.isInStatusState( ers, variantsStatusMap );
	}

	isInStatusStateDisabled( ers: ErsVariant ): boolean {
		const variantsStatusMap: Map<EntityVariantEnum, ErsStatus> =
			new Map<EntityVariantEnum, ErsStatus>( [
				[ EntityVariantEnum.EXTERNAL, ErsExternalStatusEnum.DISABLED ],
				[ EntityVariantEnum.INTERNAL, ErsStatusEnum.DISABLED ]
			] );

		return this.isInStatusState( ers, variantsStatusMap );
	}


	// STORE ###########################################################################
	// SELECTORS
	selectAllERS$(): Observable<ErsVariant[]> {
		return this.store$.pipe( select<Record<string, ErsVariant[]>, ErsVariant[]>( ersStore.selectors.adapter.all ) );
	}

	selectERSById$( ersId ): Observable<ErsVariant> {
		return this.store$.pipe( select<Record<string, ErsVariant>, ErsVariant>( ersStore.selectors.selectERSById( ersId ) ) );
	}

	selectERSByBusinessPremisesId$( businessPremisesId: string, entityVariant?: EntityVariantEnum ): Observable<ErsVariant[]> {
		return this.store$.pipe(
			select<Record<string, ErsVariant[]>, ErsVariant[]>( ersStore.selectors.selectERSByBusinessPremisesId( businessPremisesId, entityVariant ) ) );
	}

	selectListByEntityVariant$( entityVariant: EntityVariantEnum ): Observable<ErsVariant[]> {
		return this.store$.pipe( select<Record<string, ErsVariant[]>, ErsVariant[]>( ersStore.selectors.selectErsListByEntityVariant( entityVariant ) ) );
	}

	selectErsByIdAndEntityVariant$( ersId: string, entityVariant: EntityVariantEnum ): Observable<ErsVariant> {
		return this.store$.pipe( select<Record<string, ErsVariant>, ErsVariant>( ersStore.selectors.selectErsByIdAndEntityVariant( ersId, entityVariant ) ) );
	}

	// UPDATERS
	updateStoreState( updateErsState: IPropsUpdateErsState ): void {
		this.store$.dispatch( ersStore.actions.updateErsState( updateErsState ) );
	}


	// NAVIGATION #################################################################################
	navigateBack(
		options: {
			caller: string,
			navigateBackFallback?: PresetNavigateBackConfig,
			shouldRestoreListOperationData?: boolean
		}
	): void {
		const {
			caller = '',
			navigateBackFallback,
			shouldRestoreListOperationData = true
		} = options;

		this.logger.debug( options?.caller );

		const fallbackRouteDefinition: RouteDefinition =
			this.routerService.getFallbackRoute(
				navigateBackFallback?.fallbackRoute.basePath,
				navigateBackFallback?.fallbackRoute.path,
				navigateBackFallback?.fallbackRoute.params
			);

		if ( navigateBackFallback?.listOperationsId ) {
			this.listOperationsService.state.listOperationsSetRestoreFlag(
				navigateBackFallback?.listOperationsId,
				shouldRestoreListOperationData,
				caller
			);
		}

		this.routerService.navigateBack(
			fallbackRouteDefinition,
			caller + ' | ErsService:navigateBack'
		);
	}

	navigateToListPage( presetId: ErsPresetEnum, businessPremisesId?: string ): void {
		if ( presetId ) {

			let routeDefinition: IRouteDefinition;
			const ersListPageBaseRoute: Omit<ErsListPageBaseRoute, 'path'> = {
				basePath: RouteBasePath.Ers
			};

			switch ( presetId ) {
				case ErsPresetEnum.BUSINESS_PREMISES_DETAIL_ERS:
					const businessPremisesDetailPageProductTabRoute: BusinessPremisesDetailPageProductTabRoute = {
						basePath: RouteBasePath.BusinessPremises,
						path: BusinessPremisesRoutesEnum.DetailPageProductTab,
						params: {
							businessPremisesId,
							product: ProductEnum.FISCAL,
							tab: AllowedDetailsTabEnum.ERS
						},
						replaceLastStoredRouteWithNew: true
					};
					routeDefinition = businessPremisesDetailPageProductTabRoute;
					break;

				case ErsPresetEnum.BUSINESS_PREMISES_DETAIL_ERS_EXTERNAL:
					const businessPremisesDetailPageProductTabVariantRoute: BusinessPremisesDetailPageProductTabVariantRoute = {
						basePath: RouteBasePath.BusinessPremises,
						path: BusinessPremisesRoutesEnum.DetailPageProductTabVariant,
						params: {
							businessPremisesId,
							product: ProductEnum.FISCAL,
							tab: AllowedDetailsTabEnum.ERS,
							variant: EntityVariantEnum.EXTERNAL
						},
						replaceLastStoredRouteWithNew: true
					};
					routeDefinition = businessPremisesDetailPageProductTabVariantRoute;
					break;

				case ErsPresetEnum.ERS:
					const ersListPageRoute: ErsListPageRoute = {
						...ersListPageBaseRoute,
						path: ErsRoutesEnum.ListPage
					};
					routeDefinition = ersListPageRoute;
					break;

				case ErsPresetEnum.ERS_EXTERNAL:
					const ersExternalListPageRoute: ErsExternalListPageRoute = {
						...ersListPageBaseRoute,
						path: ErsRoutesEnum.ExternalListPage
					};
					routeDefinition = ersExternalListPageRoute;
					break;
			}

			this.routerService.navigateToNextRoute(
				routeDefinition,
				'ErsService:navigateToListPage'
			);
		}
	}

	navigateToDetailPage( ersId: string, presetId: ErsPresetEnum ): void {
		if ( ersId && presetId ) {

			let routeDefinition: IRouteDefinition;
			const ersDetailPageBaseRoute: Omit<ErsDetailPageBaseRoute, 'path'> = {
				basePath: RouteBasePath.Ers,
				params: { ersId }
			};

			switch ( presetId ) {
				case ErsPresetEnum.BUSINESS_PREMISES_DETAIL_ERS:
				case ErsPresetEnum.ERS:
					const ersDetailPageRoute: ErsDetailPageRoute = {
						...ersDetailPageBaseRoute,
						path: ErsRoutesEnum.DetailPage
					};
					routeDefinition = ersDetailPageRoute;
					break;

				case ErsPresetEnum.BUSINESS_PREMISES_DETAIL_ERS_EXTERNAL:
				case ErsPresetEnum.ERS_EXTERNAL:
					const ersExternalDetailPageRoute: ErsExternalDetailPageRoute = {
						...ersDetailPageBaseRoute,
						path: ErsRoutesEnum.ExternalDetailPage
					};
					routeDefinition = ersExternalDetailPageRoute;
					break;
			}

			this.routerService.navigateToNextRoute(
				routeDefinition,
				'ErsService:navigateToDetailPage'
			);
		}
	}

	navigateToDetailPageTab(
		ersId: string,
		tab: AllowedDetailsTabEnum,
		presetId: ErsPresetEnum,
		replaceLastStoredRouteWithNew: boolean = true
	): void {
		if ( ersId && tab && presetId ) {

			let routeDefinition: IRouteDefinition;
			const ersDetailPageTabBaseRoute: Omit<ErsDetailPageTabBaseRoute, 'path'> = {
				basePath: RouteBasePath.Ers,
				params: {
					ersId,
					tab
				},
				replaceLastStoredRouteWithNew
			};

			switch ( presetId ) {
				case ErsPresetEnum.ERS:
				case ErsPresetEnum.BUSINESS_PREMISES_DETAIL_ERS:
					const ersDetailPageTabRoute: ErsDetailPageTabRoute = {
						...ersDetailPageTabBaseRoute,
						path: ErsRoutesEnum.DetailPageTab
					};
					routeDefinition = ersDetailPageTabRoute;
					break;

				case ErsPresetEnum.ERS_EXTERNAL:
				case ErsPresetEnum.BUSINESS_PREMISES_DETAIL_ERS_EXTERNAL:
					const ersExternalDetailPageTabRoute: ErsExternalDetailPageTabRoute = {
						...ersDetailPageTabBaseRoute,
						path: ErsRoutesEnum.ExternalDetailPageTab
					};
					routeDefinition = ersExternalDetailPageTabRoute;
					break;
			}

			this.routerService.navigateToNextRoute(
				routeDefinition,
				'ErsService:navigateToDetailPageTab'
			);
		}
	}

	navigateToFormCreate(
		businessPremisesId: string,
		presetId: ErsPresetEnum
	): void {
		if ( businessPremisesId && presetId ) {
			let routeDefinition: IRouteDefinition;
			const ersFormPageCreateBaseRoute: Omit<ErsFormPageCreateBaseRoute, 'path'> = {
				basePath: RouteBasePath.Ers,
				params: { businessPremisesId }
			};

			const businessPremisesDetailPageErsFormPageCreateBaseRoute: Omit<BusinessPremisesDetailPageErsFormPageCreateBaseRoute, 'path'> = {
				basePath: RouteBasePath.BusinessPremises,
				params: { businessPremisesId, product: ProductEnum.FISCAL }
			};

			switch ( presetId ) {
				case ErsPresetEnum.BUSINESS_PREMISES_DETAIL_ERS:
					const businessPremisesDetailPageErsFormPageCreateRoute: BusinessPremisesDetailPageErsFormPageCreateRoute = {
						...businessPremisesDetailPageErsFormPageCreateBaseRoute,
						path: BusinessPremisesRoutesEnum.DetailPageErsFormPageCreate
					};

					routeDefinition = businessPremisesDetailPageErsFormPageCreateRoute;
					break;

				case ErsPresetEnum.BUSINESS_PREMISES_DETAIL_ERS_EXTERNAL:
					const businessPremisesDetailPageErsExternalFormPageCreateRoute: BusinessPremisesDetailPageErsExternalFormPageCreateRoute = {
						...businessPremisesDetailPageErsFormPageCreateBaseRoute,
						path: BusinessPremisesRoutesEnum.DetailPageErsExternalFormPageCreate
					};

					routeDefinition = businessPremisesDetailPageErsExternalFormPageCreateRoute;
					break;

				case ErsPresetEnum.ERS:
					const ersFormPageCreateRoute: ErsFormPageCreateRoute = {
						...ersFormPageCreateBaseRoute,
						path: ErsRoutesEnum.FormPageCreate
					};

					routeDefinition = ersFormPageCreateRoute;
					break;

				case ErsPresetEnum.ERS_EXTERNAL:
					const ersExternalFormPageCreateRoute: ErsExternalFormPageCreateRoute = {
						...ersFormPageCreateBaseRoute,
						path: ErsRoutesEnum.ExternalFormPageCreate
					};

					routeDefinition = ersExternalFormPageCreateRoute;
					break;
			}

			this.routerService.navigateToNextRoute(
				routeDefinition,
				'ErsService:navigateToFormCreate'
			);
		}
	}

	navigateToFormUpdate(
		businessPremisesId: string,
		ersId: string,
		presetId: ErsPresetEnum
	): void {
		if ( businessPremisesId && ersId && presetId ) {
			let routeDefinition: IRouteDefinition;
			const ersFormPageUpdateBaseRoute: Omit<ErsFormPageUpdateBaseRoute, 'path'> = {
				basePath: RouteBasePath.Ers,
				params: { ersId }
			};


			const businessPremisesDetailPageErsFormPageUpdateBaseRoute: Omit<BusinessPremisesDetailPageErsFormPageUpdateBaseRoute, 'path'> = {
				basePath: RouteBasePath.BusinessPremises,
				params: { businessPremisesId, ersId, product: ProductEnum.FISCAL }
			};

			switch ( presetId ) {
				case ErsPresetEnum.BUSINESS_PREMISES_DETAIL_ERS:
					const businessPremisesDetailPageErsFormPageUpdateRoute: BusinessPremisesDetailPageErsFormPageUpdateRoute = {
						...businessPremisesDetailPageErsFormPageUpdateBaseRoute,
						path: BusinessPremisesRoutesEnum.DetailPageErsFormPageUpdate
					};
					routeDefinition = businessPremisesDetailPageErsFormPageUpdateRoute;
					break;

				case ErsPresetEnum.BUSINESS_PREMISES_DETAIL_ERS_EXTERNAL:
					const businessPremisesDetailPageErsExternalFormPageUpdateRoute: BusinessPremisesDetailPageErsExternalFormPageUpdateRoute = {
						...businessPremisesDetailPageErsFormPageUpdateBaseRoute,
						path: BusinessPremisesRoutesEnum.DetailPageErsExternalFormPageUpdate
					};
					routeDefinition = businessPremisesDetailPageErsExternalFormPageUpdateRoute;
					break;

				case ErsPresetEnum.ERS:
					const ersFormPageUpdateRoute: ErsFormPageUpdateRoute = {
						...ersFormPageUpdateBaseRoute,
						path: ErsRoutesEnum.FormPageUpdate
					};
					routeDefinition = ersFormPageUpdateRoute;
					break;

				case ErsPresetEnum.ERS_EXTERNAL:
					const ersExternalFormPageUpdateRoute: ErsExternalFormPageUpdateRoute = {
						...ersFormPageUpdateBaseRoute,
						path: ErsRoutesEnum.ExternalFormPageUpdate
					};
					routeDefinition = ersExternalFormPageUpdateRoute;
					break;
			}

			this.routerService.navigateToNextRoute(
				routeDefinition,
				'ErsService:navigateToFormUpdate'
			);
		}
	}

	// API #################################################################################
	// Helper
	private executeApiRequestByPresetId<T extends Observable<RequestCompleteEntityState<ErsVariant> | RequestCompleteListState<ErsVariant>>>(
		presetId: ErsPresetEnum,
		ersApi: () => T,
		ersExternalApi: () => T
	): T {
		switch ( presetId ) {
			case ErsPresetEnum.BUSINESS_PREMISES_DETAIL_ERS:
			case ErsPresetEnum.ERS:
				return ersApi();

			case ErsPresetEnum.BUSINESS_PREMISES_DETAIL_ERS_EXTERNAL:
			case ErsPresetEnum.ERS_EXTERNAL:
				return ersExternalApi();
		}
	}

	// List Requests
	list(
		options: {
			presetId: ErsPresetEnum,
			storeConfig?: IStoreConfig
		}
	): Observable<RequestCompleteListState<ErsVariant>> {
		const {
			presetId,
			storeConfig
		} = options;

		const storeProcessingConfig = this.createListStoreProcessingConfig<ErsVariant>(
			storeConfig,
			this.storeList$(),
			StoreActionsEnum.UPSERT_MANY
		);

		const req$: Observable<RequestCompleteListState<ErsVariant>> = this.executeApiRequestByPresetId<Observable<RequestCompleteListState<ErsVariant>>>(
			presetId,
			() => this._ersApi.list(),
			() => this._ersExternalApi.list()
		);

		return this.requestListThroughProxy<ErsVariant>( req$, storeProcessingConfig );
	}

	searchWithStoredListOperationsData(
		options: {
			businessPremisesId: string,
			caller: string,
			listOperationsId: ListOperationsIdentifierEnum,
			presetId: ErsPresetEnum,
			skipErsActionItemChecks?: boolean,
			storeConfig?: IStoreConfig
		}
	): Observable<RequestCompleteListState<ErsVariant>> {

		const {
			businessPremisesId,
			caller,
			listOperationsId,
			presetId,
			skipErsActionItemChecks,
			storeConfig
		} = options;

		let apiReqFn: ( ...args: any[] ) => Observable<RequestCompleteListState<ErsVariant>>;
		let args: any[];
		let indexForListOperationsArgsArray: number;

		switch ( listOperationsId ) {
			case ListOperationsIdentifierEnum.BUSINESS_PREMISES_DETAIL_PAGE_FISKAL_ERSES:
			case ListOperationsIdentifierEnum.BUSINESS_PREMISES_DETAIL_PAGE_FISKAL_ERSES_EXTERNAL:
				apiReqFn = ( [ presetIdArg, businessPremisesIdArg, listOperationsArg, callerArg, storeConfigArg, skipErsActionItemChecksArg ] ) =>
					this.listByBPremisesId(
						{
							businessPremisesId: businessPremisesIdArg,
							caller: callerArg,
							params: listOperationsArg as IErsHttpParams,
							presetId: presetIdArg,
							skipErsActionItemChecks: skipErsActionItemChecksArg,
							storeConfig: storeConfigArg
						}
					);

				args = [
					presetId,
					businessPremisesId,
					`${ caller } | ErsService:searchWithStoredListOperationsData->listByBPremisesId`,
					storeConfig,
					skipErsActionItemChecks
				];

				indexForListOperationsArgsArray = 2;
				break;

			case ListOperationsIdentifierEnum.ERS_LIST_PAGE:
			case ListOperationsIdentifierEnum.ERS_EXTERNAL_LIST_PAGE:
				apiReqFn = ( [ presetIdArg, listOperationsArg, callerArg, storeConfigArg ] ) =>
					this.search(
						{
							caller: callerArg,
							params: listOperationsArg as IErsHttpParams,
							presetId: presetIdArg,
							storeConfig: storeConfigArg
						}
					);
				args = [ presetId, `${ caller } | ErsService:searchWithStoredListOperationsData->search`, storeConfig ];
				indexForListOperationsArgsArray = 1;
				break;
		}

		return this.listOperationsService.helper.requestWithStoredListOperationsData<ErsVariant, IErsHttpParams>(
			apiReqFn,
			args,
			indexForListOperationsArgsArray,
			listOperationsId
		);
	}

	// TODO before providing storeConfig param a store search selector is needed
	//  for filtering store entries by params
	search(
		options: {
			caller: string,
			params?: IErsHttpParams,
			presetId: ErsPresetEnum,
			storeConfig?: IStoreConfig
		}
	): Observable<RequestCompleteListState<ErsVariant>> {
		const {
			caller,
			presetId,
			storeConfig
		} = options;

		let { params } = options;

		// default storeConfig setup for search as long as no search selector exists
		const defaultStoreConfig: IStoreConfig = {
			getFromStoreFirst: false,
			updateStore: true
		};

		let storeProcessingConfig: IStoreProcessingConfig<ErsVariant[]> = this.createListStoreProcessingConfig<ErsVariant>(
			storeConfig ? storeConfig : defaultStoreConfig,
			this.storeList$<ErsVariant>(),
			StoreActionsEnum.SET_ALL
		);

		storeProcessingConfig = { ...storeProcessingConfig, caller: `${ caller } | ErsService:search` };

		params = this.generateParams( params );
		const req$: Observable<RequestCompleteListState<ErsVariant>> = this.executeApiRequestByPresetId<Observable<RequestCompleteListState<ErsVariant>>>(
			presetId,
			() => this._ersApi.search( { params } ),
			() => this._ersExternalApi.search( { params } )
		);

		return this.requestListThroughProxy<ErsVariant>( req$, storeProcessingConfig );
	}

	listByBPremisesId(
		options: {
			businessPremisesId: string,
			caller: string,
			getAllPagesData?: boolean,
			params?: IErsHttpParams,
			presetId: ErsPresetEnum,
			skipErsActionItemChecks?: boolean,
			storeConfig?: IStoreConfig
		}
	): Observable<RequestCompleteListState<ErsVariant>> {
		const {
			businessPremisesId,
			caller,
			getAllPagesData,
			presetId,
			skipErsActionItemChecks,
			storeConfig
		} = options;

		let { params } = options;

		// default storeConfig setup for search as long as no search selector exists
		const defaultStoreConfig: IStoreConfig = {
			getFromStoreFirst: false,
			updateStore: true
		};

		let storeProcessingConfig: IStoreProcessingConfig<ErsVariant[]> = this.createListStoreProcessingConfig<ErsVariant>(
			storeConfig ? storeConfig : defaultStoreConfig,
			this.storeList$<ErsVariant>(),
			storeConfig?.storeAction ?? StoreActionsEnum.SET_ALL
		);

		storeProcessingConfig = { ...storeProcessingConfig, caller: `${ caller } | ErsService:listByBPremisesId` };

		params = this.generateParams( params );

		// tslint:disable-next-line:variable-name
		let getAllPagesReq$: ( [ string, IErsHttpParams ] ) => Observable<RequestCompleteListState<ErsVariant>>;
		let variantReq$: Observable<RequestCompleteListState<ErsVariant>>;

		switch ( presetId ) {
			case ErsPresetEnum.ERS:
			case ErsPresetEnum.BUSINESS_PREMISES_DETAIL_ERS:
				variantReq$ = this._ersApi.listByBusinessPremisesId( { businessPremisesId, params } );
				getAllPagesReq$ = ( [ businessPremisesIdArg, paramsArg ] ) =>
					this._ersApi.listByBusinessPremisesId( { businessPremisesId: businessPremisesIdArg, params: paramsArg } );
				break;

			case ErsPresetEnum.ERS_EXTERNAL:
			case ErsPresetEnum.BUSINESS_PREMISES_DETAIL_ERS_EXTERNAL:
				variantReq$ = this._ersExternalApi.listByBusinessPremisesId( { businessPremisesId, params } );
				getAllPagesReq$ = ( [ businessPremisesIdArg, paramsArg ] ) =>
					this._ersExternalApi.listByBusinessPremisesId( { businessPremisesId: businessPremisesIdArg, params: paramsArg } );
				break;
		}

		const req$: Observable<RequestCompleteListState<ErsVariant>> =
			getAllPagesData
			? this.requestAllPagesData<ErsVariant>( getAllPagesReq$, [ businessPremisesId, params ], 1 )
			: variantReq$;

		const proxyReq$ = this.requestListThroughProxy<ErsVariant>( req$, storeProcessingConfig );

		proxyReq$.pipe( take( 1 ) )
		.subscribe( ( ersState: RequestCompleteListState<ErsVariant> ) => {
			if ( ersState.requestState === RequestState.SUCCESS && !skipErsActionItemChecks ) {
				ersState.list.forEach( ers => this.checkAndUpdateEntityState( ers, `${ caller } | ErsService:listByBPremisesId->response` ) );
			}
		} );

		return proxyReq$;
	}

	listByTssId(
		options: {
			caller: string,
			getAllPagesData?: boolean,
			params?: IErsHttpParams,
			presetId: ErsPresetEnum,
			skipErsActionItemChecks?: boolean,
			storeConfig?: IStoreConfig,
			tssId: string
		}
	): Observable<RequestCompleteListState<ErsVariant>> {
		const {
			caller,
			getAllPagesData,
			presetId,
			skipErsActionItemChecks = true,
			storeConfig,
			tssId
		} = options;

		let { params } = options;

		// default storeConfig setup for search as long as no search selector exists
		const defaultStoreConfig: IStoreConfig = {
			getFromStoreFirst: false,
			updateStore: true
		};

		let storeProcessingConfig = this.createListStoreProcessingConfig<Ers>(
			storeConfig ? storeConfig : defaultStoreConfig,
			this.storeList$<Ers>(),
			StoreActionsEnum.SET_ALL
		);

		storeProcessingConfig = { ...storeProcessingConfig, caller: `${ caller } | ErsService:listByTssId` };

		params = this.generateParams( params );

		// tslint:disable-next-line:variable-name
		let getAllPagesReq$: ( [ string, IErsHttpParams ] ) => Observable<RequestCompleteListState<ErsVariant>>;
		let variantReq$: Observable<RequestCompleteListState<ErsVariant>>;

		switch ( presetId ) {
			case ErsPresetEnum.ERS:
			case ErsPresetEnum.BUSINESS_PREMISES_DETAIL_ERS:
				variantReq$ = this._ersApi.listByTssId( { tssId, params } );
				getAllPagesReq$ = ( [ tssIddArg, paramsArg ] ) =>
					this._ersApi.listByTssId( { tssId: tssIddArg, params: paramsArg } );
				break;

			case ErsPresetEnum.ERS_EXTERNAL:
			case ErsPresetEnum.BUSINESS_PREMISES_DETAIL_ERS_EXTERNAL:
				variantReq$ = this._ersExternalApi.listByTssId( { tssId, params } );
				getAllPagesReq$ = ( [ tssIddArg, paramsArg ] ) =>
					this._ersExternalApi.listByTssId( { tssId: tssIddArg, params: paramsArg } );
				break;
		}

		const req$: Observable<RequestCompleteListState<ErsVariant>> =
			getAllPagesData
			? this.requestAllPagesData<ErsVariant>( getAllPagesReq$, [ tssId, params ], 1 )
			: variantReq$;

		const proxyReq$ = this.requestListThroughProxy<ErsVariant>( req$, storeProcessingConfig );

		proxyReq$.pipe( take( 1 ) )
		.subscribe( ( ersState: RequestCompleteListState<ErsVariant> ) => {
			if ( ersState.requestState === RequestState.SUCCESS && !skipErsActionItemChecks ) {
				ersState.list.forEach( ers => this.checkAndUpdateEntityState( ers, `${ caller } | ErsService:listByTssId->response` ) );
			}
		} );

		return proxyReq$;
	}

	// Entity Requests
	read(
		options: {
			id: string,
			observableReturnType?: ObservableTypeEnum,
			presetId: ErsPresetEnum,
			skipErsActionItemChecks?: boolean,
			storeConfig?: IStoreConfig,
			withFullResponse?: boolean
		}
	): Observable<RequestCompleteEntityState<ErsVariant>> {
		const {
			id,
			observableReturnType,
			presetId,
			skipErsActionItemChecks = true,
			withFullResponse
		} = options;

		let {
			storeConfig
		} = options;

		// request from store must be skipped when withFullResponse is set true
		storeConfig = {
			getFromStoreFirst: withFullResponse ? false : storeConfig?.getFromStoreFirst,
			updateStore: true
		};

		const storeProcessingConfig: IStoreProcessingConfig<ErsVariant> = this.createEntityStoreProcessingConfig<ErsVariant>(
			storeConfig ? storeConfig : storeConfig,
			this.storeEntityById$<ErsVariant>( id ),
			StoreActionsEnum.UPSERT_ONE
		);

		const req$: Observable<RequestCompleteEntityState<ErsVariant>> = this.executeApiRequestByPresetId<Observable<RequestCompleteEntityState<ErsVariant>>>(
			presetId,
			() => this._ersApi.read( { ersId: id, withFullResponse } ),
			() => this._ersExternalApi.read( { ersId: id, withFullResponse } )
		);

		const proxyReq$ = this.requestEntityThroughProxy<ErsVariant>( id, req$, storeProcessingConfig, observableReturnType );

		proxyReq$.pipe( take( 1 ) )
		.subscribe( ( ersState: RequestCompleteEntityState<ErsVariant> ) => {
			if ( ersState.requestState === RequestState.SUCCESS && !skipErsActionItemChecks ) {
				this.checkAndUpdateEntityState( ersState.entity, `ErsService:read->response` );
			}
		} );

		return proxyReq$;
	}

	create(
		options: {
			ers: ErsVariant,
			presetId: ErsPresetEnum,
			storeConfig?: IStoreConfig
		}
	): Observable<RequestCompleteEntityState<ErsVariant>> {
		const {
			ers,
			presetId,
			storeConfig
		} = options;

		const storeProcessingConfig: IStoreProcessingConfig<ErsVariant> = this.createEntityStoreProcessingConfig<ErsVariant>(
			storeConfig,
			null,
			StoreActionsEnum.UPSERT_ONE
		);

		const req$: Observable<RequestCompleteEntityState<ErsVariant>> = this.executeApiRequestByPresetId<Observable<RequestCompleteEntityState<ErsVariant>>>(
			presetId,
			() => this._ersApi.create( { ers: ers as Ers } ),
			() => this._ersExternalApi.create( { ers: ers as ErsExternal } )
		);

		return this.requestEntityThroughProxy<ErsVariant>( null, req$, storeProcessingConfig );
	}

	update(
		options: {
			ers: ErsVariant,
			presetId: ErsPresetEnum,
			storeConfig?: IStoreConfig,
			withFullResponse?: boolean
		}
	): Observable<RequestCompleteEntityState<ErsVariant>> {
		const {
			ers,
			presetId,
			storeConfig,
			withFullResponse
		} = options;

		const storeProcessingConfig: IStoreProcessingConfig<ErsVariant> = this.createEntityStoreProcessingConfig<ErsVariant>(
			storeConfig,
			this.storeEntityById$( ers.id ),
			StoreActionsEnum.UPSERT_ONE
		);

		const req$: Observable<RequestCompleteEntityState<ErsVariant>> = this.executeApiRequestByPresetId<Observable<RequestCompleteEntityState<ErsVariant>>>(
			presetId,
			() => this._ersApi.update( { ers: ers as Ers, withFullResponse } ),
			() => this._ersExternalApi.update( { ers: ers as ErsExternal, withFullResponse } )
		);

		return this.requestEntityThroughProxy<ErsVariant>( ers.id, req$, storeProcessingConfig );
	}

	delete(
		options: {
			ersId: string,
			presetId: ErsPresetEnum,
			storeConfig?: IStoreConfig
		}
	): Observable<RequestCompleteEntityState<ErsVariant>> {
		const {
			ersId,
			presetId,
			storeConfig
		} = options;

		const storeProcessingConfig: IStoreProcessingConfig<ErsVariant> = this.createEntityStoreProcessingConfig<ErsVariant>(
			storeConfig,
			this.storeEntityById$( ersId ),
			StoreActionsEnum.REMOVE_ONE
		);

		const req$: Observable<RequestCompleteEntityState<ErsVariant>> = this.executeApiRequestByPresetId<Observable<RequestCompleteEntityState<ErsVariant>>>(
			presetId,
			() => this._ersApi.delete( { ersId } ),
			() => this._ersExternalApi.delete( { ersId } )
		);
		return this.requestEntityThroughProxy( ersId, req$, storeProcessingConfig );
	}

	private changeStatus(
		ers: ErsVariant,
		status: ErsStatus,
		storeConfig: IStoreConfig = null,
		presetId: ErsPresetEnum
	): Observable<RequestCompleteEntityState<ErsVariant>> {
		const defaultStoreConfig: IStoreConfig = {
			getFromStoreFirst: false,
			updateStore: true
		};

		const storeProcessingConfig: IStoreProcessingConfig<ErsVariant> = this.createEntityStoreProcessingConfig<ErsVariant>(
			storeConfig ? storeConfig : defaultStoreConfig,
			this.storeEntityById$( ers.id ),
			StoreActionsEnum.UPSERT_ONE
		);

		const req$: Observable<RequestCompleteEntityState<ErsVariant>> = this.executeApiRequestByPresetId<Observable<RequestCompleteEntityState<ErsVariant>>>(
			presetId,
			() => this._ersApi.updateStatus( {
				ers: { ...ers as Ers, status: status as ErsStatusEnum },
				withFullResponse: true
			} ),
			() => this._ersExternalApi.update( {
				ers: { ...ers as ErsExternal, status: status as ErsExternalStatusEnum },
				withFullResponse: true
			} )
		);

		return this.requestEntityThroughProxy<ErsVariant>( ers.id, req$, storeProcessingConfig );
	}
}
